﻿import {isNullOrEmpty} from "@2d-front-monorepo/core";
import {DependencyList, RefObject, useLayoutEffect, useRef} from "react";

export interface MinMax {
	min: number;
	max: number;
	percent: number;
}

function getMinMax(table: HTMLTableElement): MinMax[] {
	const tableHeaders = table.querySelectorAll<HTMLTableRowElement>("table th");
	const minMax: MinMax[] = [];
	const tableSize = table.clientWidth;
	let colGroup = table.querySelector<HTMLTableColElement>("colgroup");
	if (colGroup) {
		colGroup.innerHTML = "";
	} else {
		colGroup = document.createElement("colgroup");
	}

	for (const header of tableHeaders) {
		const definedSize = header.dataset["size"];
		const columnSize = header.clientWidth;
		const percent = columnSize / tableSize;
		const col = document.createElement("col");
		col.style.width = `${columnSize}px`;
		colGroup.appendChild(col);

		if (definedSize) {
			let [min, max] = definedSize.split("-");
			if (isNullOrEmpty(min)) {
				min = "0";
			}

			if (isNullOrEmpty(max)) {
				max = columnSize.toString(10);
				if (min === "0") {
					min = max;
				}
			}

			minMax.push({min: parseInt(min, 10), max: parseInt(max, 10), percent});

		} else {
			minMax.push({min: 0, max: Number.MAX_SAFE_INTEGER, percent});
		}
	}

	table.style.tableLayout = "fixed";
	table.insertBefore(colGroup, table.firstChild);
	return minMax;
}

export function calculateTableSizes(columns: MinMax[], availableSize: number): number[] {
	const sizes = new Array(columns.length);
	const fixedIndices = new Set<number>();
	const flexibleColumns: { column: MinMax; index: number }[] = [];
	let fixedSize = 0;
	let totalPercent = 0;
	let widestColumnIndex = -1;
	let widestColumnPercent = 0;

	for (let index = 0; index < columns.length; index++) {
		const column = columns[index];
		if (column.min === column.max) {
			fixedIndices.add(index);
			fixedSize += column.min;
			sizes[index] = column.min;
		} else {
			flexibleColumns.push({column, index});
			totalPercent += column.percent;
			if (column.percent > widestColumnPercent) {
				widestColumnPercent = column.percent;
				widestColumnIndex = index;
			}
		}
	}

	const remainingSpace = availableSize - fixedSize;

	if (remainingSpace < 0) {
		for (let i = 0; i < flexibleColumns.length; i++) {
			sizes[flexibleColumns[i].index] = flexibleColumns[i].column.min;
		}
		return sizes;
	}

	if (totalPercent === 0 || flexibleColumns.length === 0) {
		for (let i = 0; i < flexibleColumns.length; i++) {
			sizes[flexibleColumns[i].index] = flexibleColumns[i].column.min;
		}
		return sizes;
	}

	for (let i = 0; i < flexibleColumns.length; i++) {
		const {column, index} = flexibleColumns[i];
		const proportionalSize = (column.percent / totalPercent) * remainingSpace;
		sizes[index] = Math.round(Math.max(column.min, Math.min(column.max, proportionalSize)));
	}

	let totalAllocated = 0;
	for (let i = 0; i < flexibleColumns.length; i++) {
		totalAllocated += sizes[flexibleColumns[i].index];
	}
	let remainingAdjustment = remainingSpace - totalAllocated;

	while (Math.abs(remainingAdjustment) > 0.1) {
		const redistributableColumns: { column: MinMax; index: number }[] = [];
		for (let i = 0; i < flexibleColumns.length; i++) {
			const {column, index} = flexibleColumns[i];
			const currentSize = sizes[index];
			if (remainingAdjustment > 0 ? currentSize < column.max : currentSize > column.min) {
				redistributableColumns.push(flexibleColumns[i]);
			}
		}

		if (redistributableColumns.length === 0) break;

		const spacePerColumn = remainingAdjustment / redistributableColumns.length;
		let actualDistributed = 0;

		for (let i = 0; i < redistributableColumns.length; i++) {
			const {column, index} = redistributableColumns[i];
			const currentSize = sizes[index];
			const newSize = currentSize + spacePerColumn;
			const clampedSize = Math.round(Math.max(column.min, Math.min(column.max, newSize)));
			actualDistributed += clampedSize - currentSize;
			sizes[index] = clampedSize;
		}

		remainingAdjustment -= actualDistributed;
		if (Math.abs(actualDistributed) < 0.1) break;
	}

	let totalCalculatedWidth = 0;
	for (let i = 0; i < sizes.length; i++) {
		totalCalculatedWidth += sizes[i];
	}
	const widthDiscrepancy = availableSize - totalCalculatedWidth;

	if (Math.abs(widthDiscrepancy) >= 1 && widestColumnIndex !== -1 && !fixedIndices.has(widestColumnIndex)) {
		const adjustedWidth = sizes[widestColumnIndex] + widthDiscrepancy;
		const column = columns[widestColumnIndex];
		sizes[widestColumnIndex] = Math.max(column.min, Math.min(column.max, adjustedWidth));
	}

	return sizes;
}

function applyResizing(element: HTMLElement, availableSize: number, minMax: MinMax[]): void {
	const sizes = calculateTableSizes(minMax, availableSize);
	const colNodes = element.querySelectorAll<HTMLElement>("table colgroup col");
	for (let index = 0; index < colNodes.length; index++) {
		colNodes[index].style.width = `${sizes[index]}px`;
	}
}

const cache = new WeakMap<HTMLElement, MinMax[]>();

export function useTableSizing(mainRef: RefObject<HTMLElement | null> | HTMLElement, deps: DependencyList): void {
	const prevDeps = useRef<DependencyList>(undefined);
	const depsChanged = !prevDeps.current || deps.some((dep, i) => !Object.is(dep, prevDeps.current![i]));

	useLayoutEffect(() => {
		const element = mainRef == null ? null : mainRef instanceof HTMLElement ? mainRef : mainRef.current;
		if (!element) return;

		const table = element.querySelector<HTMLTableElement>("table")!;
		if (depsChanged) {
			prevDeps.current = deps;
			table.style.tableLayout = "auto";
		}

		const cached = cache.get(element);
		let minMax: MinMax[];
		if (cached == null || depsChanged) {
			minMax = getMinMax(table);
			cache.set(element, minMax);
			table.style.tableLayout = "fixed";
		} else {
			minMax = cached;
		}

		const observer = new ResizeObserver(entries => {
			const current = entries.length === 1 ? entries[0] : entries.find(x => x.target === element);
			if (!current) return;
			applyResizing(element, current.contentBoxSize[0].inlineSize, minMax);
		});

		observer.observe(element);
		applyResizing(element, element.clientWidth, minMax);

		// useLayoutEffect runs before first paint, so on a cold load the measurement above sees
		// unloaded web fonts (icon ligatures lay out as wide raw text) and freezes those widths
		// into the colgroup. Re-measure once the fonts are in.
		let disposed = false;
		if (document.fonts.status !== "loaded") {
			document.fonts.ready.then(() => {
				if (disposed) return;
				table.style.tableLayout = "auto";
				minMax = getMinMax(table);
				cache.set(element, minMax);
				applyResizing(element, element.clientWidth, minMax);
			});
		}

		return () => {
			disposed = true;
			observer.disconnect();
		};

	}, [mainRef, ...deps]);
}
